import { Component, computed, inject, input, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { Router } from '@angular/router';
import { BaseDataGridComponent } from '@components/base-data-grid.component';
import { PageHeaderComponent } from '@components/page-header/page-header.component';
import { BulkPriceUpdateRequest, PricingBulkOperationsFormComponent } from '@components/pricing-bulk-operations-form/pricing-bulk-operations-form.component';
import { PricingEntityShortcutsComponent } from '@components/pricing-entity-shortcuts/pricing-entity-shortcuts.component';
import { dateFormattingOptions } from '@domain/formatting';
import { CreatePriceRequest, HaulierItem, PartnerType, Pricing, ServiceProductItem, SlaughterhouseItem } from '@hjc/masterdata-types';
import { TranslateModule } from '@ngx-translate/core';
import { AddPriceComponent, AddPriceData, AddPriceResult } from '@pages/pricing/components/add-price/add-price.component';
import { DialogHelpersService } from '@services/dialog/dialog-helpers.service';
import { NotificationService } from '@services/notification.service';
import { PartnersService } from '@services/partners.service';
import { PricingService } from '@services/pricing.service';
import { ServicesService } from '@services/services.service';
import { AgGridAngular } from 'ag-grid-angular';
import { ColDef, GridApi, RowClickedEvent, RowSelectionOptions, SelectionChangedEvent, themeAlpine } from 'ag-grid-community';
import Fuse from 'fuse.js';
import { isEmpty } from 'lodash-es';
import { debounceTime, Subscription } from 'rxjs';

@Component({
  selector: 'md-list-prices-for-services-page',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    AgGridAngular,
    PageHeaderComponent,
    PricingBulkOperationsFormComponent,
    PricingEntityShortcutsComponent,
    TranslateModule
  ],
  templateUrl: './list-prices-for-services-page.component.html',
  styleUrl: './list-prices-for-services-page.component.scss',
  standalone: true
})
export class ListPricesForServicesPageComponent extends BaseDataGridComponent implements OnInit {
  // Inject services
  private servicesService = inject(ServicesService);
  private pricingService = inject(PricingService);
  private partnersService = inject(PartnersService);
  private notificationService = inject(NotificationService);
  private dialogHelpers = inject(DialogHelpersService);
  private router = inject(Router);

  serviceId = input<string>('');

  pageTitle = signal<string>('');
  allPrices = signal<Pricing[]>([]);
  displayPrices = signal<Pricing[]>([]);
  selectedPrices = signal<Pricing[]>([]);
  partners = signal<(HaulierItem | SlaughterhouseItem)[]>([]);
  service = signal<ServiceProductItem | undefined>(undefined);
  isLoading = signal<boolean>(false);
  isExporting = signal<boolean>(false);
  theme = themeAlpine;

  searchInput = new FormControl<string>('', { nonNullable: true });
  searchTermSubscription?: Subscription;
  fuse?: Fuse<Pricing>;

  rowSelection: RowSelectionOptions = {
    mode: 'multiRow'
  };

  columnDefs = computed(() => this.getColumnDefs());
  private gridApi = signal<GridApi<Pricing> | null>(null);

  constructor() {
    super();
    this.searchInput.valueChanges.pipe(debounceTime(300), takeUntilDestroyed()).subscribe(term => this.search(term));
  }

  ngOnInit(): void {
    const id = this.serviceId();
    if (id) {
      this.loadData(id);
    }
  }

  private getColumnDefs(): ColDef<Pricing>[] {
    const getTranslatedHeader = (key: string) => (this.translationsReady() ? this.translate.instant(key) : key);

    return [
      {
        headerName: getTranslatedHeader('FIELDS.CURRENT_PRICE'),
        valueGetter: p => `${p.data?.currentRate?.rate} ${p.data?.unit}`,
        sortable: false,
        flex: 1
      },
      { headerName: getTranslatedHeader('FIELDS.PARTNER_ID'), field: 'partnerId', sortable: true, flex: 1 },
      {
        headerName: getTranslatedHeader('FIELDS.PARTNER_TYPE'),
        valueFormatter: p => this.translate.instant(`PARTNER_TYPES.${p.data?.partnerType.toUpperCase()}`),
        field: 'partnerType',
        sortable: true,
        flex: 1
      },
      { headerName: getTranslatedHeader('FIELDS.PARTNER_NAME'), field: `partnerName.${this.lang()}`, sortable: true, flex: 1 },
      {
        headerName: getTranslatedHeader('FIELDS.VALID_FROM'),
        field: 'currentRate.validFrom',
        valueFormatter: p => new Date(p.value).toLocaleDateString(this.lang()),
        sortable: true,
        flex: 1
      },
      { headerName: getTranslatedHeader('FIELDS.SYSTEM.CREATED_BY'), valueGetter: p => p.data?.createdBy, sortable: true, filter: true, flex: 1 },
      {
        headerName: getTranslatedHeader('FIELDS.SYSTEM.CREATED_AT'),
        field: 'createdAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 1
      },
      { headerName: getTranslatedHeader('FIELDS.SYSTEM.UPDATED_BY'), valueGetter: p => p.data?.updatedBy, sortable: true, filter: true, flex: 1 },
      {
        headerName: getTranslatedHeader('FIELDS.SYSTEM.UPDATED_AT'),
        field: 'updatedAt',
        valueFormatter: p => new Date(p.value).toLocaleString(this.lang(), dateFormattingOptions),
        sortable: true,
        flex: 1
      }
    ];
  }

  async loadData(id: string) {
    this.isLoading.set(true);

    try {
      const [service, prices] = await Promise.all([this.servicesService.getServiceById(id), this.pricingService.getPricingForService(id)]);
      this.service.set(service.data);
      this.pageTitle.set(service ? service.data.name[this.lang()] : 'Tuntematon');
      this.allPrices.set(prices.data || []);
      this.displayPrices.set(prices.data || []);

      this.initFuse();
    } catch (error) {
      console.error('Failed to load byproduct data', error);
      this.notificationService.error(this.translate.instant('NOTIFICATIONS.DATA_LOAD_FAILURE'));
    } finally {
      this.isLoading.set(false);
    }
  }

  initFuse() {
    this.fuse = new Fuse<Pricing>(this.allPrices(), {
      keys: [`product.productGroup.name.${this.lang()}`, `product.name.${this.lang()}`, `product.productType.name.${this.lang()}`],
      threshold: 0.3
    });
  }

  search(term: string) {
    if (!this.fuse) return;

    this.displayPrices.set(!term || term.length === 0 ? this.allPrices() : this.fuse.search(term).map(r => r.item));
  }

  onSelectionChanged(event: SelectionChangedEvent<Pricing>) {
    this.selectedPrices.set(event.api.getSelectedRows());
  }

  async handleBulkPriceUpdate(request: BulkPriceUpdateRequest) {
    this.isLoading.set(true);

    try {
      const response = await this.pricingService.updatePrices(request.itemIds, request.changeFactor, request.changeType, request.validFrom);

      if (response.data?.updatedPrices?.length) {
        this.mergeUpdatedPrices(response.data.updatedPrices);
        this.notificationService.success(this.translate.instant('NOTIFICATIONS.SUCCESS.PRICE_UPDATE_SUCCESS'));
      } else {
        await this.loadData(this.serviceId());
        this.notificationService.success(this.translate.instant('NOTIFICATIONS.SUCCESS.PRICE_UPDATE_SUCCESS'));
      }
    } catch (error) {
      console.error('Failed to update prices:', error);
      this.notificationService.error(this.translate.instant('NOTIFICATIONS.ERROR.PRICE_UPDATE_FAILURE'));
    } finally {
      this.isLoading.set(false);
    }
  }

  private mergeUpdatedPrices(updatedPrices: Pricing[]) {
    if (!updatedPrices.length) return;

    const updatedPriceMap = new Map(updatedPrices.map(price => [price.id, price]));

    this.allPrices.update(prices => prices.map(price => (updatedPriceMap.has(price.id) ? updatedPriceMap.get(price.id)! : price)));

    if (this.searchInput.value) {
      this.search(this.searchInput.value);
    } else {
      this.displayPrices.update(prices => prices.map(price => (updatedPriceMap.has(price.id) ? updatedPriceMap.get(price.id)! : price)));
    }

    this.initFuse();
  }

  async refreshPriceData(data: Pricing) {
    this.isLoading.set(true);

    try {
      this.allPrices.update(prices => [...prices, data]);

      if (this.searchInput.value && this.searchInput.value.length > 0) {
        this.search(this.searchInput.value);
      } else {
        this.displayPrices.set(this.allPrices());
      }

      this.initFuse();
    } catch (error) {
      console.error('Error refreshing price data:', error);
      this.notificationService.error(this.translate.instant('NOTIFICATIONS.ERROR.PRICING_UPDATE_FAILED'));
    } finally {
      this.isLoading.set(false);
    }
  }

  async showAddPriceDialog() {
    this.isLoading.set(true);

    try {
      if (isEmpty(this.partners())) {
        const slaughterhouses = await this.partnersService.getSlaughterhousePartners();
        const hauliers = await this.partnersService.getLogisticsPartners();
        this.partners.set([...slaughterhouses.data, ...hauliers.data]);
      }

      const partnersList = this.partners();
      if (!partnersList || partnersList.length === 0) {
        this.notificationService.error(this.translate.instant('NOTIFICATIONS.ERROR.MISSING_PRODUCT_DATA'));
        return;
      }

      const existingPartnerIds = new Set(this.allPrices().map(p => p.partnerId));
      const availablePartners = partnersList.filter(partner => !existingPartnerIds.has(partner.id));

      if (!availablePartners.length) {
        this.notificationService.error(this.translate.instant('NOTIFICATIONS.ERROR.ALL_PARTNERS_HAVE_PRICES'));
        return;
      }

      const dialogData: AddPriceData = {
        items: [...availablePartners],
        measure: [...this.pricingService.measures]
      };

      await this.dialogHelpers.openAndHandleResult<AddPriceComponent, AddPriceData, AddPriceResult>(
        AddPriceComponent,
        { width: '600px', data: dialogData },
        async result => {
          const priceObj: CreatePriceRequest = {
            name: result.itemName,
            productId: this.serviceId(),
            productName: this.service()!.name,
            productType: 'serviceProduct',
            partnerId: result.itemId,
            partnerName: result.itemName,
            partnerType: result.itemType as PartnerType,
            unit: result.unit,
            rates: [{ rate: result.price, validFrom: result.validFrom.toISOString() }]
          };
          const response = await this.pricingService.addPrice(priceObj);

          this.notificationService.success(this.translate.instant('NOTIFICATIONS.SUCCESS.PRICE_ADD_SUCCESS'));
          await this.refreshPriceData(response.data);
        },
        this.translate.instant('NOTIFICATIONS.ERROR.PRICE_ADD_FAILURE')
      );
    } catch (error) {
      console.error('Failed to prepare dialog:', error);
      this.notificationService.error(this.translate.instant('NOTIFICATIONS.ERROR.FORM_OPEN_FAILURE'));
    } finally {
      this.isLoading.set(false);
    }
  }

  onRowClicked(event: RowClickedEvent<Pricing>) {
    if (event.data?.id) {
      this.router.navigate(['/app/pricing/services', this.serviceId(), 'price', event.data.id]);
    }
  }

  onGridReady(params: { api: GridApi<Pricing> }) {
    this.gridApi.set(params.api);

    const selectionMode = this.rowSelection.mode === 'multiRow' ? 'multiple' : 'single';
    params.api.setGridOption('rowSelection', selectionMode);
  }

  async exportToExcel() {
    const api = this.gridApi();
    if (!api) return;

    await this.exportGridToExcel(api, this.columnDefs(), 'byproduct-prices', this.isExporting);
  }
}
